import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUserCountry } from '../utils/geolocation';

const LocationContext = createContext(null);
LocationContext.displayName = 'LocationContext';

export const useLocation = () => {
    const context = useContext(LocationContext);
    if (!context) {
        throw new Error('useLocation must be used within a LocationProvider');
    }
    return context;
};

export function LocationProvider({ children }) {
    const [userCountry, setUserCountry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const detectUserLocation = async () => {
            try {
                setLoading(true);
                setError(null);

                const country = await getUserCountry();
                setUserCountry(country);

                // Store in localStorage for future visits (with expiration)
                localStorage.setItem('userCountry', JSON.stringify({
                    country,
                    timestamp: Date.now()
                }));

                console.log('User location detected:', country);
            } catch (err) {
                console.error('Failed to detect user location:', err);
                setError(err.message);

                // Try to get cached location
                const cached = getCachedLocation();
                if (cached) {
                    setUserCountry(cached);
                } else {
                    setUserCountry('global'); // Default fallback
                }
            } finally {
                setLoading(false);
            }
        };

        // Check for cached location first
        const cached = getCachedLocation();
        if (cached) {
            setUserCountry(cached);
            setLoading(false);

            // Still detect fresh location in background
            detectUserLocation();
        } else {
            detectUserLocation();
        }
    }, []);

    const getCachedLocation = () => {
        try {
            const cached = localStorage.getItem('userCountry');
            if (cached) {
                const { country, timestamp } = JSON.parse(cached);
                // Cache valid for 24 hours
                if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
                    return country;
                }
            }
        } catch (error) {
            console.warn('Failed to read cached location:', error);
        }
        return null;
    };

    const refreshLocation = async () => {
        try {
            setLoading(true);
            setError(null);

            const country = await getUserCountry();
            setUserCountry(country);

            localStorage.setItem('userCountry', JSON.stringify({
                country,
                timestamp: Date.now()
            }));

            return country;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const setSelectedCountry = (country) => {
        console.log('🌍 LocationContext: Setting country from', userCountry, 'to', country);
        setUserCountry(country);
        // Store manual selection in localStorage
        localStorage.setItem('userCountry', JSON.stringify({
            country,
            timestamp: Date.now(),
            manualSelection: true
        }));
    };

    const value = {
        userCountry,
        loading,
        error,
        refreshLocation,
        setUserCountry: setSelectedCountry,
        isMexico: userCountry === 'mx',
        isUS: userCountry === 'us',
        isCanada: userCountry === 'ca',
        isSpain: userCountry === 'es',
        isArgentina: userCountry === 'ar',
        isColombia: userCountry === 'co',
        isChile: userCountry === 'cl',
        isPeru: userCountry === 'pe',
        isBrazil: userCountry === 'br',
        isGlobal: userCountry === 'global' || !userCountry
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
}

export default LocationContext;
